import * as XLSX from "xlsx";

// Converts a Date object to yyyy-mm-dd for input[type="date"]
export const formatDateForInput = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Converts a string (dd-mm-yyyy, dd/mm/yyyy, or yyyy-mm-dd) to yyyy-mm-dd for input[type="date"]
export const convertToDateInput = (dateStr?: string): string => {
  if (!dateStr) return "";
  const parts = dateStr.split(/[\/-]/);
  if (parts.length !== 3) return "";
  const isYFirst = parts[0].length === 4;
  const [a, b, c] = parts;
  const yyyy = isYFirst ? a : c.length === 2 ? `20${c}` : c;
  const mm = (isYFirst ? b : b).padStart(2, "0");
  const dd = (isYFirst ? c : a).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Converts a string (dd-mm-yyyy, dd/mm/yyyy, or yyyy-mm-dd) to dd-mm-yyyy for display/storage
// export const convertToDisplayDate = (dateStr?: string): string => {
//   if (!dateStr) return "";
//   const parts = dateStr.split(/[\/-]/);
//   if (parts.length !== 3) return "";
//   const isYFirst = parts[0].length === 4;
//   const [a, b, c] = parts;
//   const yyyy = isYFirst ? a : c.length === 2 ? `20${c}` : c;
//   const mm = (isYFirst ? b : b).padStart(2, "0");
//   const dd = (isYFirst ? c : a).padStart(2, "0");
//   return `${dd}-${mm}-${yyyy}`;
// };

export const convertToDisplayDate = (value?: unknown): string => {
  if (value === null || value === undefined || value === "") return "";

  const formatDate = (date: Date): string => {
    if (Number.isNaN(date.getTime())) return "";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${date.getFullYear()}`;
  };

  if (value instanceof Date) return formatDate(value);

  // Firestore Timestamp values expose a toDate() method.
  if (
    typeof value === "object" &&
    "toDate" in value &&
    typeof value.toDate === "function"
  ) {
    return formatDate(value.toDate());
  }

  if (typeof value !== "string") return "";

  const dateStr = value.trim();
  const numericDate = dateStr.match(
    /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$|^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/,
  );
  if (numericDate) {
    const yyyy = numericDate[1] || numericDate[6];
    const mm = numericDate[2] || numericDate[5];
    const dd = numericDate[3] || numericDate[4];
    return `${dd.padStart(2, "0")}-${mm.padStart(2, "0")}-${yyyy}`;
  }

  // Example: "July 19, 2026 at 3:37:32 PM UTC+8".
  const normalizedDate = dateStr
    .replace(/\s+at\s+/i, " ")
    .replace(/\s+UTC([+-])(\d{1,2})(?::?(\d{2}))?$/i, (_, sign, hours, minutes) =>
      ` GMT${sign}${String(hours).padStart(2, "0")}${minutes || "00"}`,
    );

  return formatDate(new Date(normalizedDate));
};

// Returns today's date as dd-mm-yyyy
export const formatToday = (): string => {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();
  // return `${dd}-${mm}-${yyyy}`;
  return `${yyyy}-${mm}-${dd}`;
};

// Checks if a string is a valid date, returns "-" if invalid
export const isValidDate = (dateStr?: string): string => {
  if (!dateStr) return "-";
  const parts = dateStr.split("-");
  if (parts.length !== 3 || parts.some((p) => !p)) return "-";
  return dateStr;
};

export const ddMmYyyyToYyyyMmDd = (dateStr?: string): string | undefined => {
  if (!dateStr) return undefined;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return undefined;

  const [dd, mm, yyyy] = parts;
  if (!dd || !mm || !yyyy) return undefined;

  return `${yyyy}-${mm}-${dd}`;
};

// Convert any value (Date | number | string) → "dd-mm-yyyy" for display/storage
export const toDdMmYyyy = (val: any): string | undefined => {
  try {
    if (val === null || val === undefined || val === "") return undefined;

    // Date object
    if (val instanceof Date && !isNaN(val.getTime())) {
      const d = val;
      return `${String(d.getDate()).padStart(2, "0")}-${String(
        d.getMonth() + 1
      ).padStart(2, "0")}-${d.getFullYear()}`;
    }

    // Numeric Excel date codes or timestamp
    if (typeof val === "number") {
      if (val > 1) {
        const parsed = XLSX.SSF.parse_date_code(Number(val));
        if (parsed) {
          const d = new Date(parsed.y, parsed.m - 1, parsed.d);
          if (!isNaN(d.getTime())) {
            return `${String(d.getDate()).padStart(2, "0")}-${String(
              d.getMonth() + 1
            ).padStart(2, "0")}-${d.getFullYear()}`;
          }
        }
      }
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        return `${String(d.getDate()).padStart(2, "0")}-${String(
          d.getMonth() + 1
        ).padStart(2, "0")}-${d.getFullYear()}`;
      }
    }

    // String date parsing
    if (typeof val === "string") {
      const trimmed = val.trim();
      if (!trimmed) return undefined;

      // ISO timestamp
      if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
        const d = new Date(trimmed);
        if (!isNaN(d.getTime())) {
          return `${String(d.getDate()).padStart(2, "0")}-${String(
            d.getMonth() + 1
          ).padStart(2, "0")}-${d.getFullYear()}`;
        }
      }

      // Slash-based formats DD/MM/YYYY or MM/DD/YYYY
      const slashRegex =
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/;
      const match = trimmed.match(slashRegex);
      if (match) {
        const part1 = parseInt(match[1], 10);
        const part2 = parseInt(match[2], 10);
        const year = match[3];
        let day = part1;
        let month = part2;

        if (part2 > 12) {
          day = part2;
          month = part1;
        } else if (part1 > 12) {
          day = part2;
          month = part1;
        }
        return `${String(day).padStart(2, "0")}-${String(month).padStart(
          2,
          "0"
        )}-${year}`;
      }

      // Fallback to Date constructor
      const dateAttempt = new Date(trimmed);
      if (!isNaN(dateAttempt.getTime())) {
        return `${String(dateAttempt.getDate()).padStart(2, "0")}-${String(
          dateAttempt.getMonth() + 1
        ).padStart(2, "0")}-${dateAttempt.getFullYear()}`;
      }
    }
  } catch (err) {
    console.warn("Date conversion error:", err, "Value:", val);
  }

  return undefined;
};
